import type { Site } from '@datadog/browser-core';
import { isClientSide } from '~/common/utils/is-client-side';

export type Environment = {
  production: boolean;
  demo: boolean;
  staging: boolean;
  apiUrlPrefix: string;
  chatApiUrlPrefix: string;
  gpiClientId: string;
  auth0: {
    domain: string;
    client_id: string;
    redirect_uri: string;
    audience: string;
  };
  datadog: {
    site?: Site;
    logs: {
      clientToken?: string;
    };
    rum: {
      applicationId?: string;
      clientToken?: string;
    };
  };
  googleTagManager: {
    gtm_id: string;
    gtm_auth?: string;
    gtm_preview?: string;
  };
  zendesk: {
    widgetKey?: string;
  };
};
type EnvironmentName = 'stg' | 'dem' | 'prd';

/**
 * ローカル開発は ENV_ID(0〜9) でポートをずらした並行起動に対応しており、
 * フロント 8020+ENV_ID*100 と API 8080+ENV_ID*100 の対応関係を全 ENV_ID 分そろえる必要があるため機械生成する。
 * ポート規約は loglass-tools/env-config.sh と一致させること。
 */
const localhostEnvironmentEntries: Record<string, Environment> = Object.fromEntries(
  Array.from({ length: 10 }, (_, envId) => {
    const webPort = 8020 + envId * 100;
    const apiPort = 8080 + envId * 100;
    const localhostEnvironment: Environment = {
      production: false,
      demo: false,
      staging: false,
      apiUrlPrefix: `http://localhost:${apiPort}`,
      chatApiUrlPrefix: 'http://localhost:3001',
      gpiClientId: '188891869490-lc2csdmcflun9tgep4u0a0h8i4tiat7a.apps.googleusercontent.com',
      auth0: {
        domain: 'local-loglass2.auth0.com',
        client_id: 'wRY7PRygGMLTqdb8RBIk2qw6YOd33BTn',
        redirect_uri: `http://localhost:${webPort}/login/callback`,
        audience: 'https://local-loglass2-api.loglass.jp',
      },
      datadog: {
        site: undefined,
        logs: {
          clientToken: undefined,
        },
        rum: {
          applicationId: undefined,
          clientToken: undefined,
        },
      },
      googleTagManager: {
        gtm_id: 'GTM-KS8KKVP',
        gtm_auth: 'OKDMwJLRbyLT5r9UPrMsVg',
        gtm_preview: 'env-4',
      },
      zendesk: {
        widgetKey: '9af0ac35-65a8-45bc-9a68-b2ddf7eda859',
      },
    };
    return [`http://localhost:${webPort}`, localhostEnvironment] as const;
  }),
);

const environment: Record<string, Environment> = {
  ...localhostEnvironmentEntries,
  'https://loglass.com': {
    production: true,
    demo: false,
    staging: false,
    apiUrlPrefix: 'https://loglass-api.loglass.com',
    chatApiUrlPrefix: 'https://loglass-ai-chat.loglass.com',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'prd-loglass.auth0.com',
      client_id: 'hLYyuzzSKmSNmBPs2R2tCZd170cGx4YM',
      redirect_uri: 'https://loglass.com/login/callback',
      audience: 'https://prd-loglass-api.loglass.com',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '391ebf34-a8f6-4ca7-b65a-8eba9c189f7b',
        clientToken: 'pub49a9faffae4977ed1631d518abfaea02',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
    },
    zendesk: {
      widgetKey: '9af0ac35-65a8-45bc-9a68-b2ddf7eda859',
    },
  },
  'https://demoglass.xyz': {
    production: false,
    demo: false,
    staging: true,
    apiUrlPrefix: 'https://loglass-api.demoglass.xyz',
    chatApiUrlPrefix: 'https://loglass-ai-chat.demoglass.xyz',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'stg-loglass.auth0.com',
      client_id: 'nfcRV20sNdYiP9Er4C3Wlu0sibEHVpcV',
      redirect_uri: 'https://demoglass.xyz/login/callback',
      audience: 'https://stg-loglass-api.loglass.jp',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
      gtm_auth: 'OcXotokAIz95AuOfSQYFcQ',
      gtm_preview: 'env-3',
    },
    zendesk: {
      widgetKey: 'b78979f6-9411-4f21-ad89-f9eb35060c97',
    },
  },
  'https://www-testf.demoglass.xyz': {
    production: false,
    demo: false,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testf.demoglass.xyz',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testf.demoglass.xyz',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'stg-loglass.auth0.com',
      client_id: 'wI6fq9L02dNlaYMveKGV69GE5SLf7x8U',
      redirect_uri: 'https://www-testf.demoglass.xyz/login/callback',
      audience: 'https://stg-loglass-api.loglass.jp',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
      gtm_auth: 'OcXotokAIz95AuOfSQYFcQ',
      gtm_preview: 'env-3',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-testp.demoglass.xyz': {
    production: false,
    demo: false,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testp.demoglass.xyz',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testp.demoglass.xyz',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'stg-loglass.auth0.com',
      client_id: 'z4YnfNmRfIwm5H1VYOdQ4owVEPmuXUGX',
      redirect_uri: 'https://www-testp.demoglass.xyz/login/callback',
      audience: 'https://stg-loglass-api.loglass.jp',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
      gtm_auth: 'OcXotokAIz95AuOfSQYFcQ',
      gtm_preview: 'env-3',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-testz.demoglass.xyz': {
    production: false,
    demo: false,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testz.demoglass.xyz',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testz.demoglass.xyz',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'stg-loglass.auth0.com',
      client_id: '4kegBDdHUUkV1khwYmDERAQVXaiSMcXZ',
      redirect_uri: 'https://www-testz.demoglass.xyz/login/callback',
      audience: 'https://stg-loglass-api.loglass.jp',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
      gtm_auth: 'OcXotokAIz95AuOfSQYFcQ',
      gtm_preview: 'env-3',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-testh.demoglass.xyz': {
    production: false,
    demo: false,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testh.demoglass.xyz',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testh.demoglass.xyz',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'stg-loglass.auth0.com',
      client_id: 'VVCWscyMFDPOYESQlgMQmfEg3Vfs48Hx',
      redirect_uri: 'https://www-testh.demoglass.xyz/login/callback',
      audience: 'https://stg-loglass-api.loglass.jp',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
      gtm_auth: 'OcXotokAIz95AuOfSQYFcQ',
      gtm_preview: 'env-3',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-testr.demoglass.xyz': {
    production: false,
    demo: false,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testr.demoglass.xyz',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testr.demoglass.xyz',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'stg-loglass.auth0.com',
      client_id: 'gLmXTUHVeS1l3wnk0iaFQH7kljQvSwLy',
      redirect_uri: 'https://www-testr.demoglass.xyz/login/callback',
      audience: 'https://stg-loglass-api.loglass.jp',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
      gtm_auth: 'OcXotokAIz95AuOfSQYFcQ',
      gtm_preview: 'env-3',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-testc.demoglass.xyz': {
    production: false,
    demo: false,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testc.demoglass.xyz',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testc.demoglass.xyz',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'stg-loglass.auth0.com',
      client_id: 'Bx8BR1ZUxm6vvtEEukvDmMiolZSEcDpI',
      redirect_uri: 'https://www-testc.demoglass.xyz/login/callback',
      audience: 'https://stg-loglass-api.loglass.jp',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
      gtm_auth: 'OcXotokAIz95AuOfSQYFcQ',
      gtm_preview: 'env-3',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-testb.demoglass.xyz': {
    production: false,
    demo: false,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testb.demoglass.xyz',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testb.demoglass.xyz',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'stg-loglass.auth0.com',
      client_id: 'i34LMnkebrO4yJqba5YgYDltI5NJKNDV',
      redirect_uri: 'https://www-testb.demoglass.xyz/login/callback',
      audience: 'https://stg-loglass-api.loglass.jp',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
      gtm_auth: 'OcXotokAIz95AuOfSQYFcQ',
      gtm_preview: 'env-3',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-testo.demoglass.xyz': {
    production: false,
    demo: false,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testo.demoglass.xyz',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testo.demoglass.xyz',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'stg-loglass.auth0.com',
      client_id: '4sNDDIeyucsH73EHwl6wv2RA3tpFaLPY',
      redirect_uri: 'https://www-testo.demoglass.xyz/login/callback',
      audience: 'https://stg-loglass-api.loglass.jp',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
      gtm_auth: 'OcXotokAIz95AuOfSQYFcQ',
      gtm_preview: 'env-3',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://demo-loglass.com': {
    production: false,
    demo: true,
    staging: true,
    apiUrlPrefix: 'https://loglass-api.demo-loglass.com',
    chatApiUrlPrefix: 'https://loglass-ai-chat.demo-loglass.com',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'dem-loglass.us.auth0.com',
      client_id: 'agrDaei7xWkjqmwzVMBjDuNmekZ1YiT7',
      redirect_uri: 'https://demo-loglass.com/login/callback',
      audience: 'https://dem-loglass-api.demo-loglass.com',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-sub.demo-loglass.com': {
    production: false,
    demo: true,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-extra.demo-loglass.com',
    chatApiUrlPrefix: 'https://loglass-ai-chat-extra.demo-loglass.com',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'dem-loglass.us.auth0.com',
      client_id: 'o6wIT8pUEY9qHhqIPTjyjTS5rLeCk1Gi',
      redirect_uri: 'https://www-sub.demo-loglass.com/login/callback',
      audience: 'https://dem-loglass-api.demo-loglass.com',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-testo.demo-loglass.com': {
    production: false,
    demo: true,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testo.demo-loglass.com',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testo.demo-loglass.com',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'dem-loglass.us.auth0.com',
      client_id: 'gLs3tWmPWs6R4lPull7nrmBsgp63Sx8W',
      redirect_uri: 'https://www-testo.demo-loglass.com/login/callback',
      audience: 'https://dem-loglass-api.demo-loglass.com',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-testp.demo-loglass.com': {
    production: false,
    demo: true,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testp.demo-loglass.com',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testp.demo-loglass.com',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'dem-loglass.us.auth0.com',
      client_id: 'Pj9tgwegteRnyQFutxnazpsegciQoOmj',
      redirect_uri: 'https://www-testp.demo-loglass.com/login/callback',
      audience: 'https://dem-loglass-api.demo-loglass.com',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
  'https://www-testz.demo-loglass.com': {
    production: false,
    demo: true,
    staging: true,
    apiUrlPrefix: 'https://loglass-api-testz.demo-loglass.com',
    chatApiUrlPrefix: 'https://loglass-ai-chat-testz.demo-loglass.com',
    gpiClientId: '188891869490-qp7gfsg4iu56fmlq9ea0vok1t4nbdi4u.apps.googleusercontent.com',
    auth0: {
      domain: 'dem-loglass.us.auth0.com',
      client_id: 'wpf26FgybaOxGNMiVYXTOWKQJPb2c2Dh',
      redirect_uri: 'https://www-testz.demo-loglass.com/login/callback',
      audience: 'https://dem-loglass-api.demo-loglass.com',
    },
    datadog: {
      site: 'datadoghq.com',
      logs: {
        clientToken: 'pub7a62578ff12a4f74af3e506b8c3cded6',
      },
      rum: {
        applicationId: '72e1f164-c3f2-4154-a8b7-17d652e29927',
        clientToken: 'pubbec0967716ce072064e12ade32a6000c',
      },
    },
    googleTagManager: {
      gtm_id: 'GTM-KS8KKVP',
    },
    zendesk: {
      widgetKey: undefined,
    },
  },
};

const defaultEnvironment: Environment = {
  production: false,
  demo: false,
  staging: false,
  apiUrlPrefix: '',
  chatApiUrlPrefix: '',
  gpiClientId: '',
  auth0: {
    domain: '',
    client_id: '',
    redirect_uri: '',
    audience: '',
  },
  datadog: {
    site: undefined,
    logs: {
      clientToken: undefined,
    },
    rum: {
      applicationId: undefined,
      clientToken: undefined,
    },
  },
  googleTagManager: {
    gtm_id: '',
    gtm_auth: undefined,
    gtm_preview: undefined,
  },
  zendesk: {
    widgetKey: undefined,
  },
};

export const env = (): Environment | undefined => {
  if (!isClientSide()) return;
  return environment[window.location.origin] ?? defaultEnvironment;
};

export const getEnvironmentName = (env?: Environment): EnvironmentName | undefined => {
  if (!env || !isClientSide()) return;
  if (env.staging) return 'stg';
  if (env.demo) return 'dem';
  if (env.production) return 'prd';
  return;
};
